import { type TFunction } from 'i18next';

import TicketStatusTableCell from './TicketStatusTableCell';
import { DashboardTableProps } from 'src/components/dashboard-table/DashboardTable';
import { TriageTicket } from 'src/services/triage/types';

export const PARAM_SEARCH_NAME = 'ticket_search_keyword';
export const PARAM_FILTER_NAME = 'ticket_filter';

export const FILTER_STATUS_KEY = 'ticket_status_';
export const FILTER_HELPDESK_KEY = 'written_to_helpdesk_';
export const FILTER_REASON_KEY = 'reason_for_no_prediction_';
export const FILTER_CORRECT_PREDICTION_KEY = 'correct_prediction_filter_';

export const FILTER_STATUS = {
  CLOSED: `${FILTER_STATUS_KEY}closed`,
  IN_PROGRESS: `${FILTER_STATUS_KEY}in_progress`,
  OPEN: `${FILTER_STATUS_KEY}open`,
  RESOLVED: `${FILTER_STATUS_KEY}resolved`,
};

export const FILTER_HELP_DESK = {
  NO: `${FILTER_HELPDESK_KEY}no`,
  YES: `${FILTER_HELPDESK_KEY}yes`,
};

export const FILTER_CORRECT_PREDICTION = {
  NO: `${FILTER_CORRECT_PREDICTION_KEY}no`,
  YES: `${FILTER_CORRECT_PREDICTION_KEY}yes`,
};

export const getTableColumns = (
  t: TFunction<'triage'>,
): DashboardTableProps<TriageTicket>['columns'] => [
  {
    key: 'timestamp',
    size: 220,
    title: t('modelDetail.ticketsTable.columns.predictionDate'),
    valueType: 'timestamp_ms',
  },
  {
    key: 'id',
    size: 210,
    title: t('modelDetail.ticketsTable.columns.ticketId'),
  },
  {
    key: 'title',
    size: 300,
    title: t('modelDetail.ticketsTable.columns.ticketTitle'),
  },
  {
    key: 'body',
    rows: 3,
    size: 300,
    title: t('modelDetail.ticketsTable.columns.ticketBody'),
  },
  {
    Component: TicketStatusTableCell,
    key: 'status',
    size: 160,
    title: t('modelDetail.ticketsTable.columns.ticketStatus'),
  },
  {
    key: 'predicted_label',
    rows: 2,
    size: 300,
    title: t('modelDetail.ticketsTable.columns.predictedLabel'),
  },
  {
    key: 'current_label',
    rows: 2,
    size: 300,
    title: t('modelDetail.ticketsTable.columns.groundTruthLabel'),
    tooltip: t('modelDetail.ticketsTable.columns.groundTruthLabelTooltip'),
  },
  {
    key: 'is_prediction_accurate',
    size: 200,
    title: t('modelDetail.ticketsTable.columns.correctPrediction'),
    valueType: 'boolean_yn',
  },
  {
    key: 'prediction_reasoning',
    size: 300,
    title: t('modelDetail.ticketsTable.columns.predictionReasoning'),
  },
  {
    key: 'reason_no_prediction',
    size: 360,
    title: t('modelDetail.ticketsTable.columns.reasonNoPrediction'),
  },
  {
    key: 'written_to_helpdesk',
    size: 260,
    title: t('modelDetail.ticketsTable.columns.writtenToHelpdesk'),
    tooltip: t('modelDetail.ticketsTable.columns.writtenToHelpdeskTooltip'),
    valueType: 'boolean_yn',
  },
];

export const FILTER_OPTIONS_STATUS_ITEM = {
  label: 'Ticket Status',
  options: [
    {
      label: 'Open',
      value: FILTER_STATUS.OPEN,
    },
    {
      label: 'In progress',
      value: FILTER_STATUS.IN_PROGRESS,
    },
    {
      label: 'Resolved',
      value: FILTER_STATUS.RESOLVED,
    },
    {
      label: 'Closed',
      value: FILTER_STATUS.CLOSED,
    },
  ],
  value: FILTER_STATUS_KEY,
};

export const reasonForNoPredictionOptions = [
  {
    label: 'Below confidence threshhold',
    value: `${FILTER_REASON_KEY}below_confidence_threshold`,
  },
  {
    label: 'Empty input fields',
    value: `${FILTER_REASON_KEY}empty_input_fields`,
  },
  {
    label: 'Model dependencies not met',
    value: `${FILTER_REASON_KEY}model_dependencies_not_met`,
  },
];

export const FILTER_OPTIONS = [
  {
    label: 'Correct Prediction',
    options: [
      {
        label: 'Yes',
        value: FILTER_CORRECT_PREDICTION.YES,
      },
      {
        label: 'No',
        value: FILTER_CORRECT_PREDICTION.NO,
      },
    ],
    value: FILTER_CORRECT_PREDICTION_KEY,
  },
  {
    label: 'Reason for no prediction',
    options: reasonForNoPredictionOptions,
    value: FILTER_REASON_KEY,
  },
  {
    label: 'Written to Helpdesk',
    options: [
      {
        label: 'Yes',
        value: FILTER_HELP_DESK.YES,
      },
      {
        label: 'No',
        value: FILTER_HELP_DESK.NO,
      },
    ],
    value: FILTER_REASON_KEY,
  },
];

export const FILTER_KEY_FINALIZED_LABEL = 'finalized_label_filter_';
export const FILTER_LABEL_FINALIZED_LABEL = 'Finalized label';

export const FILTER_KEY_PREDICTED_LABEL = 'predicted_label_filter_';
export const FILTER_LABEL_PREDICTED_LABEL = 'Predicted label';

export const FILTER_KEY_REASON_NO_PREDICTION = 'reason_no_prediction_filter_';
export const FILTER_LABEL_REASON_NO_PREDICTION = 'Reason for no prediction';

export const INITIAL_SEARCH_TYPE = 'keyword';

export const getSearchTypeOptions = (t: TFunction<'triage'>) => [
  {
    text: t('modelDetail.ticketSearchTypes.ticket'),
    value: 'keyword',
  },
  {
    text: t('modelDetail.ticketSearchTypes.ticketId'),
    value: 'ticket_id',
  },
];

export const PARAM_SEARCH_TYPE = 'ticket_search_type';
